import { Button } from '@dev-spendesk/grapes';
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { useBudgetQuery } from 'modules/budgets/containers/BudgetOverviewPageContainer/hooks/useBudgetQuery';
import { useQueryStates } from 'src/core/api/hooks/useQueryStates';
import { HeaderWithNavigation } from 'src/core/common/components/HeaderWithNavigation/HeaderWithNavigation';
import { QueryError } from 'src/core/common/components/QueryError';
import { QuerySuspense } from 'src/core/common/components/QuerySuspense';
import { useFeature } from 'src/core/common/hooks/useFeature';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';

import { BudgetOverview } from '../../../../components/BudgetOverview';
import { useBreakdownQuery } from '../../../../containers/hooks/useBreakdownQuery';
import { useExpenseCategoriesBreakdownQuery } from '../../../../hooks/useExpenseCategoriesBreakdownQuery';
import { getBudgetLimit, type Breakdown } from '../../../../models/breakdown';
import { type BudgetaryExercise } from '../../../../models/budgetaryExercise';
import { type ExpenseCategoryBreakdown } from '../../../../models/expenseCategoryBreakdown';
import {
  type PeriodRange,
  toPeriodRangePayload,
} from '../../../../models/periodRange';
import { type SpendingClickProperty } from '../../../hooks/useSpendingClick';
import { BudgetBreakdown } from '../BudgetBreakdown';
import { BudgetaryExerciseActionsBar } from '../BudgetaryExerciseActionsBar';
import { HeaderContent } from '../BudgetaryExercisePageContent/HeaderContent';
import { BudgetsOverviewList } from '../BudgetsOverviewList';

export const BudgetOverviewPageContent = ({
  budgetaryExercise,
  budgetId,
  expenseCategory,
  onRangeChange,
  periodRange,
  spendingClick,
}: {
  budgetaryExercise: BudgetaryExercise;
  budgetId: string;
  expenseCategory?: ExpenseCategoryBreakdown['expenseCategory'];
  onRangeChange: (range: PeriodRange) => void;
  periodRange: PeriodRange;
  spendingClick: SpendingClickProperty;
}) => {
  const { t } = useTranslation('global');
  const history = useHistory();
  const companyId = useCompanyId();
  const hasBudgetByDimensionsFeature = useFeature(
    FEATURES.BUDGETS_BY_DIMENSIONS,
  );

  const periodRangePayload = useMemo(
    () => toPeriodRangePayload(periodRange),
    [periodRange],
  );

  return (
    <div className="page__container overflow-y-hidden">
      <HeaderWithNavigation links={[]}>
        {t('navigation.budgets')}
      </HeaderWithNavigation>
      <div className="flex items-center justify-between px-40 py-24">
        <Button
          type="button"
          variant="tertiaryBrand"
          iconName="chevron-left"
          hasNegativeMargins
          text={t('budget.overviewPage.back')}
          onClick={() => {
            const pageRoute = hasBudgetByDimensionsFeature
              ? routeFor(routes.BUDGETS_V2.path, {
                  company: companyId,
                })
              : routeFor(routes.BUDGETARY_EXERCISES.path, {
                  id: budgetaryExercise.id,
                  company: companyId,
                });
            history.push({
              pathname: pageRoute,
              state: {
                selectedPeriodRange: periodRange,
                highlightedBudgetId: budgetaryExercise.id,
              },
            });
          }}
        />
        <HeaderContent
          isBudgetaryExerciseSelectable={false}
          budgetaryExercise={budgetaryExercise}
        />
      </div>
      <div className="flex min-h-0 flex-1 gap-24 px-40">
        <BudgetsOverviewList
          budgetId={budgetId}
          periodRange={periodRangePayload}
        />
        <div className="border-border-default mb-24 border-0 border-r border-solid" />
        <div className="flex min-h-0 flex-1 flex-col gap-24 overflow-y-auto pb-24">
          <BudgetOverviewContent
            budgetId={budgetId}
            onRangeChange={onRangeChange}
            periodRange={periodRange}
            expenseCategory={expenseCategory}
            budgetaryExercise={budgetaryExercise}
            spendingClick={spendingClick}
          />
        </div>
      </div>
    </div>
  );
};

const BudgetOverviewContent = ({
  budgetId,
  periodRange,
  onRangeChange,
  budgetaryExercise,
  expenseCategory,
  spendingClick,
}: {
  budgetId: string;
  periodRange: PeriodRange;
  onRangeChange: (range: PeriodRange) => void;
  budgetaryExercise: BudgetaryExercise;
  expenseCategory?: ExpenseCategoryBreakdown['expenseCategory'];
  spendingClick: SpendingClickProperty;
}) => {
  const periodRangePayload = useMemo(
    () => toPeriodRangePayload(periodRange),
    [periodRange],
  );

  const queryStates = useQueryStates({
    states: {
      budget: useBudgetQuery(budgetId),
      breakdown: useBreakdownQuery(budgetId, periodRangePayload),
      expenseCategoriesBreakdown: useExpenseCategoriesBreakdownQuery(budgetId, {
        from: undefined,
        to: undefined,
      }),
    },
  });
  return (
    <QuerySuspense
      queryState={queryStates}
      fallback={(error) => (
        <QueryError queryError={error} componentType="Callout" />
      )}
      loading={null}
    >
      {({ budget, breakdown, expenseCategoriesBreakdown }) => {
        const title = expenseCategory?.id
          ? (expenseCategoriesBreakdown.expenseCategories.find(
              (ec) => ec.id === expenseCategory.id,
            )?.name ?? '')
          : budget.name;

        const expenseCategoryBreakdown =
          expenseCategoriesBreakdown.expenseCategoriesBreakdown.find(
            (ecb) => ecb.expenseCategory.id === expenseCategory?.id,
          );

        const breakdownUnified = getBreakdown(
          breakdown,
          expenseCategoryBreakdown,
        );

        return (
          <>
            <BudgetaryExerciseActionsBar
              onRangeChange={onRangeChange}
              range={periodRange}
              align="right"
              budgetaryExercise={budgetaryExercise}
              canEditBudgets={false}
              title={title}
            />
            <BudgetOverview
              name={title}
              breakdown={{
                ...breakdownUnified,
                breakdownComputedAt: breakdown.breakdownComputedAt,
              }}
              amount={getBudgetLimit(breakdownUnified)}
              isUnavailable={false}
              mode="full"
            />
            <BudgetBreakdown
              budgetId={budgetId}
              breakdown={breakdownUnified}
              breakdownComputedAt={breakdown.breakdownComputedAt}
              breakdownParameters={{
                type: 'v1',
                periodRange: periodRangePayload,
                expenseCategory,
              }}
              spendingClick={spendingClick}
            />
          </>
        );
      }}
    </QuerySuspense>
  );
};

const getBreakdown = (
  breakdown: Breakdown,
  expenseCategoryBreakdown: ExpenseCategoryBreakdown | undefined,
) => {
  if (expenseCategoryBreakdown) {
    return expenseCategoryBreakdown;
  }
  return breakdown;
};
